import { useEffect } from "react";
import { useMap } from "react-leaflet";

interface Props {
  lat: number;
  lon: number;
  /**
   * Apply a zoom as well as a centre. Omit to keep whatever zoom the user is on
   * — the original behaviour, which is right when re-centring on a geocoded
   * point the dispatcher is already looking at.
   *
   * Pass it when the CENTRE ITSELF came from a changing source, e.g. the empty
   * map's centring ladder: its rungs resolve asynchronously and differ in scale
   * (a depot at zoom 12, the operating region at zoom 6), so applying the centre
   * without its zoom would frame a country at street level.
   */
  zoom?: number;
}

/**
 * Re-centers the Leaflet map whenever lat/lon change (e.g. after geocoding).
 * Must be rendered inside a react-leaflet MapContainer.
 *
 * **This exists because `MapContainer` ignores `center`/`zoom` after mount** —
 * they are initial props only. Anything that changes the view later must go
 * through `map.setView()`, which is what this does.
 */
export function RecenterMap({ lat, lon, zoom }: Props) {
  const map = useMap();
  useEffect(() => {
    if (zoom === undefined) {
      map.setView([lat, lon]);
      return;
    }
    map.setView([lat, lon], zoom);
  }, [lat, lon, zoom, map]);
  return null;
}
